import { ModelStatus } from '../../../shared/dtos/model-status';
import { createSelectionItem, SelectionItem } from '../../../shared/dtos/selection-item';
import { SavedServiceLevelConfigurationModel } from './service-level-configuration.model';

export class ServiceLevelConfigurationGroup {
    public readonly serviceLevelID: number;
    public readonly serviceLevelName: string;

    private readonly marketSectorServiceLevelConfigMap: {[marketSectorId: string]: SavedServiceLevelConfigurationModel} = {};

    private readonly _savedConfigs: SavedServiceLevelConfigurationModel[] = [];

    public constructor(configs: SavedServiceLevelConfigurationModel[]) {
        if (configs.length < 1) {
            throw new Error('Empty config list');
        }

        const serviceLevelIds = [...new Set(configs.map(c => c.serviceLevelId))];
        const accessCodeIds = [...new Set(configs.map(c => c.accessCodeId))];

        const hasDifferentServiceLevelIds = serviceLevelIds.length > 1;
        const hasDifferentAccessCodeIds = accessCodeIds.length > 1;
        if (hasDifferentServiceLevelIds && hasDifferentAccessCodeIds) {
            throw new Error('Service level configurations contains different service levels and access codes. Please pass in configurations under the same service level and access code');
        }
        this.serviceLevelID = configs[0].serviceLevelId;
        this.serviceLevelName = configs[0].serviceLevelName;
        this._savedConfigs = configs;

        this._savedConfigs.forEach(config => {
            this.marketSectorServiceLevelConfigMap[config.marketSectorId] = config;
        });
    }

    public getAllSavedConfigs(): SavedServiceLevelConfigurationModel[] {
        return [...this._savedConfigs];
    }

    public markConfigAsDeleteByMarketSector(marketSectorId: number): void {
        const config = this.marketSectorServiceLevelConfigMap[marketSectorId];
        config.modelStatus = ModelStatus.DELETE;
    }

    public getDeletedConfigs(): number[] {
        return this._savedConfigs.filter(c => c.modelStatus === ModelStatus.DELETE).map(c => c.serviceLevelConfigId);
    }

    public getGroupingData(): {
        accessCode: string,
        marketSector: string,
        productCode: string,
        geogatingMode: string,
        geogatingType: string
    }[] {
        return this._savedConfigs.map(config => {
            return {
                accessCode: config.accessCodeName,
                marketSector: config.marketSectorName,
                productCode: config.productCode,
                geogatingMode: config.geogatingMode,
                geogatingType: config.geogatingType
            };
        });
    }

    public getUpdatedConfigs(): SavedServiceLevelConfigurationModel[] {
        return this._savedConfigs.filter(c => c.modelStatus === ModelStatus.UPDATE);
    }

    public getSavedConfigByMarketSector(marketSectorId: number): SavedServiceLevelConfigurationModel {
        return this.marketSectorServiceLevelConfigMap[marketSectorId];
    }

    public updateConfigDurations(durationIds: number[]): void {
        this.updateSelectionItems('durations', durationIds);
    }

    public updateConfigCoverageLevels(coverageLevelIds: number[]): void {
        this.updateSelectionItems('coverageLevels', coverageLevelIds);
    }

    private updateSelectionItems(name: string, selectionIds: number[]): void {
        const savedIds = this._savedConfigs
            .filter(c => c.modelStatus !== ModelStatus.DELETE)
            .map(c => c.marketSectorId);
        savedIds.forEach(marketSectorId => {
            const config : SavedServiceLevelConfigurationModel = this.getSavedConfigByMarketSector(marketSectorId);

            const key = name as keyof SavedServiceLevelConfigurationModel;
            const originalItems = config[key] as unknown as SelectionItem[];
            let originalIds = originalItems.filter(d => d.modelStatus !== ModelStatus.DELETE).map(d => d.id);
            let newIds = selectionIds.filter(i => !originalIds.includes(i));
            const deletedIds = originalIds.filter(i => !selectionIds.includes(i));
            config.modelStatus = ModelStatus.UPDATE;
            originalItems
                .filter(d => deletedIds.includes(d.id))
                .forEach(d => {
                    d.modelStatus = ModelStatus.DELETE;
                });

            // reupdate if model status has been the previously deleted/unselected then item gets reselected
            originalItems
                .filter(d => newIds.includes(d.id) && d.modelStatus === ModelStatus.DELETE)
                .forEach(d => {
                    d.modelStatus = ModelStatus.NONE;
                });
            // add all new
            originalIds = originalItems.filter(d => d.modelStatus !== ModelStatus.DELETE).map(d => d.id);
            newIds = selectionIds.filter(i => !originalIds.includes(i));

            const newItems = newIds.map(id => createSelectionItem(id, '', '', ModelStatus.NEW));
            originalItems.push(...newItems);
        });
    }

    public getAccessCodeId(): number {
        return this._savedConfigs[0].accessCodeId;
    }

    public getDurationsAsString(): string {
        return this._savedConfigs[0].getDurationsAsString();
    }

    public getCoverageLevelsAsString(): string {
        return this._savedConfigs[0].getCoverageLevelsAsString();
    }
}
